import React, { useState, useRef } from "react";
import TableHeader from "./TableHeader";
import TaskRow from "./TaskRow";

const ProjectTimeline = ({ data, setProjectData, searchQuery = "" }) => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const expandedTaskSerialRef = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [forceUpdateCounter, setForceUpdateCounter] = useState(0);
  
  // Create a stable key for forcing re-renders when data changes
  const dataKey = `${data.map(task => task.serialNumber).join('-')}-${forceUpdateCounter}`;
  
  // Utility function to renumber all tasks and their subtasks
  const renumberTasks = (tasks) => {
    return tasks.map((task, index) => {
      const newSerialNumber = index + 1;
      const updatedTask = {
        ...task,
        serialNumber: newSerialNumber,
        currentIndex: index,
      };
      
      // Renumber subtasks if they exist
      if (task.subTasks && task.subTasks.length > 0) {
        updatedTask.subTasks = task.subTasks.map((subTask, subIndex) => ({
          ...subTask,
          serialNumber: `${newSerialNumber}.${subIndex + 1}`,
          currentIndex: subIndex,
        }));
      }
      
      return updatedTask;
    });
  };

  const handleDragStart = (index) => {
    console.log(`🚀 Drag started: task ${index} (${data[index].taskName})`);
    
    setDraggedIndex(index);
    setDragOverIndex(null);
    setIsDragging(true);

    // Handle expanded task
    if (expandedIndex === index) {
      expandedTaskSerialRef.current = data[index].serialNumber;
      setExpandedIndex(null);
    } else {
      expandedTaskSerialRef.current = null;
    }
  };

  const handleDragEnter = (targetIndex) => {
    if (!isDragging || draggedIndex === null || draggedIndex === targetIndex) {
      return;
    }
    
    setDragOverIndex(targetIndex);
    console.log(`🎯 Drag over: hovering at position ${targetIndex}`);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = () => {
    // Keep the visual feedback
  };

  const handleDragEnd = () => {
    console.log(`🏁 Drag end: draggedIndex=${draggedIndex}, dragOverIndex=${dragOverIndex}`);
    
    // Validate drag operation
    if (!isDragging || draggedIndex === null || dragOverIndex === null || draggedIndex === dragOverIndex) {
      console.log('❌ Drag cancelled - invalid operation');
      resetDragState();
      return;
    }

    const sourceIndex = draggedIndex;
    const targetIndex = dragOverIndex;

    console.log(`📦 Moving task from position ${sourceIndex} to position ${targetIndex}`);
    
    try {
      // Simple array manipulation - create new array
      const newData = [...data];
      
      console.log(`📋 Before: ${newData.map((t, i) => `${i}:${t.taskName.substring(0, 10)}`).join(', ')}`);
      
      // Get the task to move
      const taskToMove = newData[sourceIndex];
      console.log(`🔄 Moving task: "${taskToMove.taskName}"`);
      
      // Remove the task from source position
      newData.splice(sourceIndex, 1);
      
      // Calculate insertion position
      let insertAt = targetIndex;
      if (sourceIndex < targetIndex) {
        // When moving down, adjust for the removed item
        insertAt = targetIndex - 1;
      }
      
      console.log(`📍 Inserting at position: ${insertAt}`);
      
      // Insert at the target position
      newData.splice(insertAt, 0, taskToMove);
      
      console.log(`📋 After: ${newData.map((t, i) => `${i}:${t.taskName.substring(0, 10)}`).join(', ')}`);
      
      // Renumber all tasks and their subtasks
      const finalData = renumberTasks(newData);
      
      console.log('📤 Updating project data...');
      console.log('🔢 Serial numbers updated:', finalData.map(t => `${t.serialNumber}:${t.taskName.substring(0, 10)}`).join(', '));
      
      // Log the data before and after for comparison
      console.log('🔍 Current data length:', data.length);
      console.log('🔍 New data length:', finalData.length);
      console.log('🔍 setProjectData function type:', typeof setProjectData);
      
      // Create a completely new object reference to ensure React detects the change
      const completelyNewData = JSON.parse(JSON.stringify(finalData));
      
      // Update the data - this should trigger re-render
      setProjectData(completelyNewData);
      
      // Force update to ensure re-render
      setForceUpdateCounter(prev => prev + 1);
      
      // Verify the update was attempted
      console.log('✅ setProjectData called with', completelyNewData.length, 'items');

      // Re-expand task if needed
      if (expandedTaskSerialRef.current) {
        setTimeout(() => {
          const newExpandedIndex = completelyNewData.findIndex(
            (task) => task.serialNumber === expandedTaskSerialRef.current
          );
          if (newExpandedIndex !== -1) {
            setExpandedIndex(newExpandedIndex);
          }
          expandedTaskSerialRef.current = null;
        }, 100);
      }
      
      console.log('✅ Drag operation completed successfully');
      
    } catch (error) {
      console.error('💥 Error during drag operation:', error);
    }
    
    resetDragState();
  };

  const resetDragState = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    setIsDragging(false);
  };

  return (
    <div style={styles.container} key={dataKey}>
      <TableHeader />
      <div style={{ height: "35px" }} />
      {data.map((task, index) => (
        <TaskRow
          key={`${task.serialNumber}-${task.taskName}-${index}`}
          task={task}
          index={index}
          setData={setProjectData}
          expandedIndex={expandedIndex}
          setExpandedIndex={setExpandedIndex}
          isDraggingOver={dragOverIndex === index}
          onDragStart={() => handleDragStart(index)}
          onDragEnter={() => handleDragEnter(index)}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDragEnd={handleDragEnd}
          searchQuery={searchQuery}
        />
      ))}
    </div>
  );
};

const styles = {
  container: {
    width: "100%",
    overflowX: "auto",
    backgroundColor: "#fff",
    marginTop: "0",
  },
};

export default ProjectTimeline;
